import React, { useEffect, useState } from 'react';
import {Button, Divider, Drawer, Stack, Typography} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { issuesService, IssueStatus, ManageIssue } from '@store/operations/issues';
import { useSnackbar } from 'notistack';
import { checkIfErrors, FieldValidationType, getFieldError } from '@utils/yup.utils';
import IssueDetailsForm from '@screens/admin/operations/noncompliance/manageIssue/components/IssueDetailsForm';
import IssueConnectForm from '@screens/admin/operations/noncompliance/manageIssue/components/IssueConnectForm';
import ManageIssueHeader from '@screens/admin/operations/noncompliance/manageIssue/components/ManageIssueHeader';
import {finalize, map} from 'rxjs';
import {SelectItem} from '@components/inputs/select/Select.component';
import AsyncSelectWithSearchComponent from '@components/inputs/asyncSelect/AsyncSelectWithSearch.component';
import {Sizes} from '@constants/sizes.constants';
import {clientsService} from '@store/clients';
import {clientsUnitsService} from '@store/clients/units';
import {suppliersService} from '@store/suppliers';
import {suppliersUnitsService} from '@store/suppliers/units';

interface ManageIssueDrawerProps {
  isCreation?: boolean;
  fromExecution?: boolean;
}

const ManageIssueDrawer = (props: ManageIssueDrawerProps) => {
  const { isCreation, fromExecution } = props;

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { executionId, issueId } = useParams();

  const [loading, setLoading] = useState(false);

  const [issue, setIssue] = useState<ManageIssue>({});
  const [status, setStatus] = useState<IssueStatus | undefined>();
  const [client, setClient] = useState<SelectItem>();
  const [clientUnit, setClientUnit] = useState<SelectItem>();

  const [supplier, setSupplier] = useState<SelectItem>();
  const [ supplierUnit, setSupplierUnit] = useState<SelectItem>();

  const handleClose = () => navigate(-1);

  function handleChangeClient(client: any) {
    setClient(client);
    setClientUnit(undefined);
  }

  function handleChangeClientUnit(clientUnit: any) {
    setClientUnit(clientUnit);
  }

  function handleChangeSupplier(supplier: any) {
    setSupplier(supplier);
    setSupplierUnit(undefined);
  }

  function handleChangeSupplierUnit(supplierUnit: any) {
    setSupplierUnit(supplierUnit);
  }

  useEffect(() => {
    if (issueId) {
      issuesService.getIssueById(issueId).subscribe({
        next: (i) => {
          setIssue(i);
          setStatus(i.status);
          console.log(i);
          if (i.clientOrganisationUnit) {
            setClientUnit({ label: i.clientOrganisationUnit?.name ?? '', value: i.clientOrganisationUnit?.id });
            setClient({ label: i.clientOrganisationUnit?.clientOrganisation?.name ?? '', value: i.clientOrganisationUnit?.clientOrganisation?.id });
          }
          if (i.supplierOrganisationUnit) {
            setSupplierUnit({ label: i.supplierOrganisationUnit?.name ?? '', value: i.supplierOrganisationUnit?.id });
            setSupplier({ label: i.supplierOrganisationUnit?.supplierOrganisation?.name ?? '', value: i.supplierOrganisationUnit?.supplierOrganisation?.id });
          }
        },
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
    }
  }, [enqueueSnackbar, issueId]);

  if (!isCreation && !issue.id) return null;

  const updateIssueField = (data: ManageIssue) => setIssue((state) => ({ ...state, ...data }));

  const handleSave = () => {
    if (isCreation && executionId) {
      setLoading(true);
      issuesService.createIssue(issue, executionId).pipe(finalize(() => setLoading(false))).subscribe({
        next: () => {
          enqueueSnackbar(t('operations.nonCompliance.success.create'), { variant: 'success' });
          handleClose();
        },
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
    } else {
      if (!issueId) {
        setLoading(true);
        issuesService.createIssueWithoutExecution({...issue, clientOrganisationUnitId: clientUnit?.value, supplierOrganisationUnitId: supplierUnit?.value}).pipe(finalize(() => setLoading(false))).subscribe({
          next: () => {
            enqueueSnackbar(t('operations.nonCompliance.success.create'), { variant: 'success' });
            handleClose();
          },
          error: (err) => enqueueSnackbar(err.text, err.options),
        });
      }
      else {
        setLoading(true);
        issuesService.updateIssue(issueId, {...issue, clientOrganisationUnitId: clientUnit?.value, supplierOrganisationUnitId: supplierUnit?.value}).pipe(finalize(() => setLoading(false))).subscribe({
          next: (newIssue) => {
            enqueueSnackbar(t('operations.nonCompliance.success.edit'), { variant: 'success' });
            setStatus(issue.status);
            setIssue(newIssue);
          },
          error: (err) => enqueueSnackbar(err.text, err.options),
        });
      }

    }
  };

  const errors = {
    issueKind: getFieldError(issue.issueKind?.id, FieldValidationType.REQUIRED_STRING),
    reportedAt: getFieldError(issue.reportedAt, FieldValidationType.REQUIRED_DATE),
    dateHour: getFieldError(issue.dateHour, FieldValidationType.REQUIRED_TIME),
    status: !isCreation ? getFieldError(issue.status, FieldValidationType.REQUIRED_STRING) : undefined,
    resolution: issue.status === IssueStatus.CLOSED ? getFieldError(issue.resolution, FieldValidationType.REQUIRED_STRING) : undefined,
    clientUnit: fromExecution ? undefined : getFieldError(clientUnit?.value, FieldValidationType.REQUIRED_STRING),
    supplierUnit: fromExecution ? undefined : getFieldError(supplierUnit?.value, FieldValidationType.REQUIRED_STRING),
  };

  return (
    <Drawer anchor="right" hideBackdrop={fromExecution} open onClose={handleClose}>
      <Stack overflow="hidden">
        <ManageIssueHeader
          handleClose={handleClose}
          issue={issue}
          status={status}
          isCreation={isCreation} />
        <Stack className="scrollable" sx={{ overflowX: 'hidden', overflowY: 'auto' }} pb="24px" pr="10px" spacing={2}>
          <IssueConnectForm
            isCreation={isCreation}
            updateIssue={updateIssueField}
            issue={issue} />
          {!fromExecution ?
              <Stack spacing={6}>
                <Stack spacing={2}>
                  <AsyncSelectWithSearchComponent
                      labelWidth={Sizes.drawerInputLabelWidth}
                      label={t('operations.executions.manage.client')}
                      placeholder={t('audits.modals.newAudit.placeholder')}
                      handleChange={handleChangeClient}
                      readOnly={!isCreation}
                      value={client ?? undefined}
                      getOptions={(search) => {
                        return clientsService.searchClients(search).pipe(
                            map((options) => options.data.map((o) => ({ label: o.name || '-', value: o.id })))
                        );
                      }} />
                  <AsyncSelectWithSearchComponent
                      labelWidth={Sizes.drawerInputLabelWidth}
                      label={t('operations.executions.manage.clientSite')}
                      placeholder={t('audits.modals.newAudit.placeholder')}
                      handleChange={handleChangeClientUnit}
                      value={clientUnit ?? undefined}
                      readOnly={!isCreation}
                      required
                      error={errors.clientUnit}
                      getOptions={(search) => {
                        return clientsUnitsService.searchClientsUnits(search, client?.value ? [client.value] : undefined).pipe(
                            map((options) => options.data.map((o) => ({ label: o.name || '-', value: o.id })))
                        );
                      }} />
                </Stack>
                <Stack spacing={2}>
                  <AsyncSelectWithSearchComponent
                      labelWidth={Sizes.drawerInputLabelWidth}
                      label={t('operations.executions.manage.supplier')}
                      placeholder={t('audits.modals.newAudit.placeholder')}
                      handleChange={handleChangeSupplier}
                      readOnly={!isCreation}
                      value={supplier ?? undefined}
                      getOptions={(search) => {
                        return suppliersService.searchSuppliers(search).pipe(
                            map((options) => options.data.map((o) => ({ label: o.name || '-', value: o.id })))
                        );
                      }} />
                  <AsyncSelectWithSearchComponent
                      labelWidth={Sizes.drawerInputLabelWidth}
                      label={t('operations.executions.manage.supplierSite')}
                      placeholder={t('audits.modals.newAudit.placeholder')}
                      handleChange={handleChangeSupplierUnit}
                      readOnly={!isCreation}
                      value={supplierUnit ?? undefined}
                      required
                      error={errors.supplierUnit}
                      getOptions={(search) => {
                        return suppliersUnitsService.searchSuppliersUnits(search, supplier?.value ? [supplier.value] : undefined).pipe(
                            map((options) => options.data.map((o) => ({ label: o.name || '-', value: o.id })))
                        );
                      }} />
                </Stack>
                <Divider variant="fullWidth" orientation="horizontal"/>
              </Stack>
              : null}

          <Stack py="20px" spacing={4}>
            <Typography fontSize={16} fontWeight="700">
              {t('operations.executions.manage.details')}
            </Typography>
            <IssueDetailsForm
              isCreation={isCreation}
              updateIssue={updateIssueField}
              issue={issue}
              errors={errors} />
            <Stack alignItems="center" alignSelf="flex-end">
              <Button
                disabled={loading || checkIfErrors(errors)}
                variant="contained" color="primary"
                onClick={handleSave}>
                {t('global.save')}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Drawer>
  );
};

export default ManageIssueDrawer;
